import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface User {
  _id: string;
  name: string;
  email: string;
}

interface RFIDTag {
  _id: string;
  tagId: string;
  type: 'personal' | 'public';
  userId?: {
    _id: string;
    name: string;
    email: string;
  };
  status: string;
  expiryDate: string;
  description?: string;
  lastUsed?: string;
  operatorId?: string;
}

interface FormData {
  tagId: string;
  type: 'personal' | 'public';
  userId: string;
  expiryDate: string;
  description: string;
}

interface RFIDTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  tag: RFIDTag | null;
}

const RFIDTagModal: React.FC<RFIDTagModalProps> = ({ isOpen, onClose, onSave, tag }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    tagId: '',
    type: 'public',
    userId: '',
    expiryDate: '',
    description: ''
  });
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(t('rfidTags.messages.usersFetchError'));
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      if (tag) {
        setFormData({
          tagId: tag.tagId,
          type: tag.type,
          userId: tag.userId?._id || '',
          expiryDate: new Date(tag.expiryDate).toISOString().split('T')[0],
          description: tag.description || ''
        });
      } else {
        resetForm();
      }
    }
  }, [isOpen, tag]);

  const resetForm = () => {
    setFormData({
      tagId: '',
      type: 'public',
      userId: '',
      expiryDate: '',
      description: ''
    });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Add validation for personal tag type
    if (formData.type === 'personal' && (!formData.userId || formData.userId === '')) {
      setError(t('rfidTags.messages.userRequired') || 'Please select a valid user');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Prepare request data - ensure userId is only included when type is personal
      const requestData: Record<string, any> = {
        ...formData,
        // If type is public, set userId to null explicitly
        userId: formData.type === 'personal' ? formData.userId : null
      };
      
      // For updates, include the status and operatorId from the original tag
      if (tag) {
        requestData.status = tag.status;
        
        // When editing, preserve the operatorId from the original tag
        // This is required by the backend RFIDTag model
        if (tag.operatorId) {
          requestData.operatorId = tag.operatorId;
        }
        
        console.log('Updating RFID tag with data:', requestData);
        await api.put(`/rfid-tags/${tag._id}`, requestData);
      } else {
        console.log('Creating new RFID tag with data:', requestData);
        await api.post('/rfid-tags', requestData);
      }
      onSave();
      onClose();
    } catch (err: any) {
      console.error('Error saving RFID tag:', err);
      console.error('Error response:', err.response?.data);
      const errorMessage = err.response?.data?.message || t('rfidTags.messages.saveError');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white p-6 rounded-lg w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {tag ? t('rfidTags.editTag') : t('rfidTags.addTag')}
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('rfidTags.tagId')}
            </label>
            <input
              type="text"
              value={formData.tagId}
              onChange={(e) => setFormData({ ...formData, tagId: e.target.value })}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('rfidTags.type')}
            </label>
            <select
              value={formData.type}
              onChange={(e) => {
                const newType = e.target.value as 'personal' | 'public';
                setFormData({ 
                  ...formData, 
                  type: newType,
                  // Reset userId when changing to public, but keep existing selection when changing to personal
                  ...(newType === 'public' ? { userId: '' } : {})
                });
                // Clear any existing error when changing type
                setError(null);
              }}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              required
            >
              <option value="public">{t('rfidTags.types.public')}</option>
              <option value="personal">{t('rfidTags.types.personal')}</option>
            </select>
          </div>

          {formData.type === 'personal' && (
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('rfidTags.assignUser')}
              </label>
              <select
                value={formData.userId}
                onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
                required={formData.type === 'personal'}
              >
                <option value="">{t('rfidTags.selectUser')}</option>
                {users.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('rfidTags.expiryDate')}
            </label>
            <input
              type="date"
              value={formData.expiryDate}
              onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('rfidTags.description')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              rows={3}
            />
          </div>

          {error && (
            <div className="mb-4 text-red-500 text-sm">{error}</div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              disabled={loading}
            >
              {loading ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RFIDTagModal;